import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, Activity, FileText, Users, HandHeart, CalendarDays, Award, ChevronDown } from 'lucide-react';
import { useHeroSliders, useStats, useEvents, useTestimonials, useBlogs, usePartners, useSiteSettings, useHighlightedEvents } from '../hooks/useContent';
import { usePageSeo } from '../hooks/usePageSeo';
import EventCard from '../components/EventCard';
import BlogCard from '../components/BlogCard';
import LoadingIndicator from '../components/LoadingIndicator';
import { contentAPI, dynamicAPI } from '../services/api';
import {
  durationLabelFromBatch as durationFromBatch,
  durationLabelFromPackages as durationFromPackages
} from '../utils/formatDurationDays';
import { resolveOfficeWhatsAppHref } from '../utils/resolveOfficeWhatsAppHref';
import { optimizeImageUrl, IMAGE_DISPLAY_WIDTH } from '../utils/optimizeImageUrl.js';
import { buildOrganizationSchema, buildWebSiteSchema, buildFaqSchema } from '../utils/seo';
import './Home.css';
import './Events.css';

const HOME_FAQS = [
  {
    question: "What is Invincible NGO?",
    answer: "Invincible NGO is India's largest youth trekking and adventure organization, founded in 2013 and headquartered in Ahmedabad, Gujarat. With over 2,50,000+ participants and 2,500+ certified volunteers, Invincible conducts affordable Himalayan snow treks, nature camps, wilderness expeditions, and youth leadership programs across India."
  },
  {
    question: "Are Invincible treks beginner-friendly and safe for first-time trekkers?",
    answer: "Yes. The majority of Invincible treks (including Kedarkantha, Brahmatal, Kasol Sar Pass, Dalhousie, and Mount Abu) are specially curated for beginners. Batches are led by certified mountaineers and Wilderness First Aid trained leaders with a 1:10 instructor-to-participant ratio and strict safety SOPs."
  },
  {
    question: "What is included in Invincible trekking fees and packages?",
    answer: "Invincible packages include accommodation (alpine tents or guest houses), all nutritious vegetarian meals, certified instructors, safety equipment (crampons, gaiters, sleeping bags), forest entry permits, and medical support. Direct base camp packages and travel options from Ahmedabad, Surat, Vadodara, and Delhi are available."
  },
  {
    question: "From which cities do Invincible treks operate departures?",
    answer: "Invincible operates group travel departures from major cities in Gujarat (Ahmedabad, Surat, Vadodara, Rajkot, Bhavnagar), Delhi, and Chandigarh. Trekkers from Mumbai, Pune, Bangalore, and across India can also join directly at the base camp location."
  },
  {
    question: "What safety precautions and medical support are provided during treks?",
    answer: "Participant safety is our highest priority. Every batch is accompanied by certified instructors equipped with first-aid kits, pulse oximeters, and emergency oxygen cylinders. Comprehensive protocols are followed for acclimatization, daily health monitoring, and emergency response."
  },
  {
    question: "What is the cancellation and refund policy for treks?",
    answer: "Invincible offers transparent cancellation and batch rescheduling policies. Participants can initiate cancellation or date transfer requests directly from their account dashboard as per the timeline specified in our booking terms."
  }
];


const FALLBACK_EVENT_IMAGE = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400';

function isDateTodayOrFuture(value, todayStamp) {
  if (!value) return false;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;
  const stamp = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime();
  return stamp >= todayStamp;
}

function hasUpcomingDate(event) {
  const today = new Date();
  const todayStamp = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();

  const candidates = [
    event?.nextBatch?.startDate,
    event?.startDate,
    ...(Array.isArray(event?.departureDates) ? event.departureDates : []),
    ...(Array.isArray(event?.batches) ? event.batches.map((batch) => batch?.startDate) : [])
  ];

  return candidates.some((dateValue) => isDateTodayOrFuture(dateValue, todayStamp));
}

function toExperienceCardTrek(trek) {
  const duration = durationFromBatch(trek.nextBatch?.startDate, trek.nextBatch?.endDate) ||
    durationFromPackages(trek.packages);
  return {
    ...trek,
    title: trek.name,
    imageUrl: optimizeImageUrl(trek.media?.[0]?.url || FALLBACK_EVENT_IMAGE, {
      width: IMAGE_DISPLAY_WIDTH.card
    }),
    shortDescription: trek.tagline || trek.description || '',
    duration
  };
}

function getYouTubeVideoId(url = '') {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const normalized = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const parsed = new URL(normalized);
    const host = parsed.hostname.replace(/^www\./i, '').toLowerCase();
    if (host === 'youtu.be') {
      return parsed.pathname.split('/').filter(Boolean)[0] || '';
    }
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
      if (parsed.pathname === '/watch') return parsed.searchParams.get('v') || '';
      if (parsed.pathname.startsWith('/shorts/')) return parsed.pathname.split('/').filter(Boolean)[1] || '';
      if (parsed.pathname.startsWith('/live/')) return parsed.pathname.split('/').filter(Boolean)[1] || '';
      if (parsed.pathname.startsWith('/v/')) return parsed.pathname.split('/').filter(Boolean)[1] || '';
      if (parsed.pathname.startsWith('/embed/')) return parsed.pathname.split('/').filter(Boolean)[1] || '';
    }
    return '';
  } catch {
    return '';
  }
}

function getYouTubeEmbedUrl(url = '') {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) return '';
  const q = new URLSearchParams({
    autoplay: '1',
    mute: '1',
    loop: '1',
    playlist: videoId,
    vq: 'hd1080',
    controls: '0',
    disablekb: '1',
    fs: '0',
    iv_load_policy: '3',
    modestbranding: '1',
    rel: '0',
    playsinline: '1',
    cc_load_policy: '0'
  });
  return `https://www.youtube.com/embed/${videoId}?${q.toString()}`;
}

const SUBCATEGORY_ICON_MAP = {
  snow: '❄️',
  car: '🚗',
  mountain: '🏔️',
  camp: '🏕️',
  cycle: '🚴‍♂️'
};

/** Max duplicate runs of the testimonial list so short content still overflows the viewport and can loop. */
const TESTIMONIAL_LOOP_MAX_COPIES = 6;

const Home = () => {
  const [activeExperience, setActiveExperience] = useState('all');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [experienceCategory, setExperienceCategory] = useState(null);
  const [experienceTreksBySlug, setExperienceTreksBySlug] = useState({});
  const [experienceLoading, setExperienceLoading] = useState(false);
  const [specialCategoryTreks, setSpecialCategoryTreks] = useState([]);
  const [statsVideoErrored, setStatsVideoErrored] = useState(false);
  const [statsImageErrored, setStatsImageErrored] = useState(false);
  const [statsMediaReady, setStatsMediaReady] = useState(false);
  const testimonialsScrollRef = useRef(null);
  const testimonialsAutoPausedRef = useRef(false);
  const testimonialsStrideRef = useRef(0);
  const highlightedScrollRef = useRef(null);
  const [testimonialLoopCopies, setTestimonialLoopCopies] = useState(3);
  const { sliders, loading: slidersLoading } = useHeroSliders();
  const { stats } = useStats();
  const { highlightedEvents, loading: highlightedLoading } = useHighlightedEvents();
  const { events: specialEvents } = useEvents({ special: true, limit: 10 });
  const { testimonials } = useTestimonials();
  const { blogs } = useBlogs(50);
  const { partners } = usePartners();
  const { settings } = useSiteSettings();
  const publicBase =
    String(settings.site_public_url || '').replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');

  const homeSchema = useMemo(() => {
    const org = buildOrganizationSchema(settings);
    const site = buildWebSiteSchema(settings, publicBase);
    const faq = buildFaqSchema(HOME_FAQS);
    return {
      '@context': 'https://schema.org',
      '@graph': [org, site, ...(faq ? [faq] : [])]
    };
  }, [settings, publicBase]);

  usePageSeo({
    title: settings.site_public_title,
    description: settings.site_meta_description,
    canonical: publicBase ? `${publicBase}/` : undefined,
    schema: homeSchema
  });


  const homeWhatsappHref = resolveOfficeWhatsAppHref(settings?.contact_phone);

  const testimonialIdsKey = useMemo(
    () => (Array.isArray(testimonials) ? testimonials.map((t) => t?.id ?? '').join(',') : ''),
    [testimonials]
  );

  useEffect(() => {
    setTestimonialLoopCopies(3);
  }, [testimonialIdsKey]);

  // Keep adding copies until the testimonial lane actually overflows (or we hit the cap).
  useLayoutEffect(() => {
    const wrap = testimonialsScrollRef.current;
    if (!wrap || !Array.isArray(testimonials) || testimonials.length === 0) return undefined;

    const bumpIfNeeded = () => {
      const sh = wrap.scrollHeight;
      const ch = wrap.clientHeight;
      setTestimonialLoopCopies((c) => {
        if (c >= TESTIMONIAL_LOOP_MAX_COPIES) return c;
        // Require clear overflow headroom for iOS where rounding can hide 1px overflow.
        if (sh <= ch + 6) return c + 1;
        return c;
      });
    };

    const id = window.requestAnimationFrame(bumpIfNeeded);
    const ro = new ResizeObserver(() => {
      window.requestAnimationFrame(bumpIfNeeded);
    });
    ro.observe(wrap);
    return () => {
      window.cancelAnimationFrame(id);
      ro.disconnect();
    };
  }, [testimonialIdsKey, testimonialLoopCopies, testimonials.length]);

  // Measure exact pixel stride of one duplicated copy, so the wrap reset is seamless.
  useLayoutEffect(() => {
    const wrap = testimonialsScrollRef.current;
    if (!wrap) return undefined;

    const measureStride = () => {
      const copies = wrap.querySelectorAll('.testimonials-copy');
      if (copies.length >= 2) {
        const stride = copies[1].offsetTop - copies[0].offsetTop;
        testimonialsStrideRef.current = stride > 0 ? stride : 0;
      } else {
        const sh = wrap.scrollHeight;
        const n = Math.max(2, testimonialLoopCopies);
        testimonialsStrideRef.current = sh > 0 ? sh / n : 0;
      }
    };

    const raf = window.requestAnimationFrame(measureStride);
    const track = wrap.querySelector('.testimonials-track');
    const ro = new ResizeObserver(() => {
      window.requestAnimationFrame(measureStride);
    });
    ro.observe(track || wrap);

    return () => {
      window.cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [testimonialIdsKey, testimonialLoopCopies]);

  // Auto-play carousel
  useEffect(() => {
    if (sliders.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % sliders.length);
      }, 5000); // Change slide every 5 seconds
      return () => clearInterval(interval);
    }
  }, [sliders.length]);

  const heroTouchStartX = useRef(null);
  const heroPointerIdRef = useRef(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % sliders.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + sliders.length) % sliders.length);
  };

  const handleHeroSwipeEnd = (clientX) => {
    const start = heroTouchStartX.current;
    heroTouchStartX.current = null;
    heroPointerIdRef.current = null;
    if (start == null || sliders.length <= 1) return;
    const delta = clientX - start;
    if (Math.abs(delta) < 48) return;
    if (delta < 0) nextSlide();
    else prevSlide();
  };

  // Infinite vertical scroll: N identical copies in JSX; wrap scrollTop by one "stripe" (scrollHeight / N) so motion never ends.
  useEffect(() => {
    if (!Array.isArray(testimonials) || testimonials.length === 0) return undefined;

    let rafId = null;
    let lastTs = 0;
    let offset = 0;
    const speedPerFrame = 0.42;

    const tick = (ts) => {
      const wrap = testimonialsScrollRef.current;
      const track = wrap?.querySelector('.testimonials-track');
      if (!wrap) {
        rafId = window.requestAnimationFrame(tick);
        return;
      }
      if (!track) {
        rafId = window.requestAnimationFrame(tick);
        return;
      }

      if (!lastTs) lastTs = ts;
      const delta = Math.min(Math.max(ts - lastTs, 0), 64);
      lastTs = ts;

      if (!testimonialsAutoPausedRef.current) {
        const stride = testimonialsStrideRef.current;
        if (stride > 0) {
          offset += (delta / 16.67) * speedPerFrame;
          if (offset >= stride) offset %= stride;
          track.style.transform = `translate3d(0, ${-offset}px, 0)`;
        } else {
          // Fallback in case stride hasn't measured yet.
          const firstCopy = track.querySelector('.testimonials-copy');
          const fallbackStride = firstCopy?.offsetHeight || 0;
          if (fallbackStride > 0) {
            offset += (delta / 16.67) * speedPerFrame;
            if (offset >= fallbackStride) offset %= fallbackStride;
            track.style.transform = `translate3d(0, ${-offset}px, 0)`;
          }
        }
      }

      rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);

    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      const wrap = testimonialsScrollRef.current;
      const track = wrap?.querySelector('.testimonials-track');
      if (track) track.style.transform = '';
    };
  }, [testimonials.length, testimonialLoopCopies]);

  const keepTestimonialsLoopSeamless = () => {
    const wrap = testimonialsScrollRef.current;
    if (!wrap) return;
    const stride = testimonialsStrideRef.current;
    if (!stride || stride <= 0) return;
    const maxScroll = wrap.scrollHeight - wrap.clientHeight;
    if (wrap.scrollTop >= maxScroll - 0.5) {
      wrap.scrollTop = Math.max(0, wrap.scrollTop - stride);
    }
    while (wrap.scrollTop >= stride - 0.5) wrap.scrollTop -= stride;
  };

  const withImageFallback = (e) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = FALLBACK_EVENT_IMAGE;
  };

  const displaySliders = sliders;

  /** Only fetch hero backgrounds for the active slide (+ next for smooth transition). */
  const heroImageLoadIndexes = useMemo(() => {
    if (!displaySliders.length) return new Set();
    if (displaySliders.length === 1) return new Set([0]);
    const next = (currentSlide + 1) % displaySliders.length;
    return new Set([currentSlide, next]);
  }, [currentSlide, displaySliders.length]);

  useEffect(() => {
    const url = optimizeImageUrl(String(displaySliders[0]?.imageUrl || '').trim(), {
      width: IMAGE_DISPLAY_WIDTH.hero
    });
    if (!url) return undefined;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = url;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, [displaySliders[0]?.imageUrl]);
  const statsVisualUrl = (settings.stats_section_image_url || '').trim();
  const statsYouTubeEmbedUrl = getYouTubeEmbedUrl(statsVisualUrl);
  const isStatsVisualYouTube = Boolean(statsYouTubeEmbedUrl);
  const isStatsVisualImage = /\.(png|jpe?g|webp|gif|svg|avif|bmp)(?:[?#].*)?$/i.test(statsVisualUrl);
  const isStatsVisualVideo = /\.(mp4|webm|ogg|mov|m4v)(?:[?#].*)?$/i.test(statsVisualUrl);
  const shouldTryStatsAsVideo = !isStatsVisualYouTube && (isStatsVisualVideo || !isStatsVisualImage);
  const communityTitle = settings.why_community_title ?? 'Community Driven';
  const communitySubtitle = settings.why_community_subtitle ?? 'Experience the spirit of togetherness in every step of the journey.';
  const hasCommunityText = Boolean(String(communityTitle || '').trim() || String(communitySubtitle || '').trim());
  const whyCommunityImageUrl = optimizeImageUrl(
    String(settings.why_community_image_url || '').trim(),
    { width: IMAGE_DISPLAY_WIDTH.hero }
  );

  const chooseExperienceHeading = useMemo(() => {
    const raw = String(settings.choose_experience_title || 'Choose Your Experience').trim();
    const words = raw.split(/\s+/).filter(Boolean);
    return {
      line1: words.slice(0, 2).join(' '),
      line2: words.slice(2).join(' ')
    };
  }, [settings.choose_experience_title]);

  useEffect(() => {
    setStatsVideoErrored(false);
    setStatsImageErrored(false);
    setStatsMediaReady(false);
  }, [statsVisualUrl]);

  const toCategoryId = (value = '') => String(value).toLowerCase().trim();
  const toCategoryLabel = (value = '') =>
    String(value)
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const selectedHomeBlogIds = useMemo(() => {
    try {
      const parsed = JSON.parse(settings.home_latest_blog_ids || '[]');
      return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
    } catch {
      return [];
    }
  }, [settings.home_latest_blog_ids]);

  const displayedBlogs = useMemo(() => {
    if (!Array.isArray(blogs) || blogs.length === 0) return [];
    if (selectedHomeBlogIds.length === 0) return blogs;
    const byId = new Map(blogs.map((blog) => [String(blog.id), blog]));
    const selected = selectedHomeBlogIds
      .map((id) => byId.get(String(id)))
      .filter(Boolean);
    return selected.length > 0 ? selected : blogs;
  }, [blogs, selectedHomeBlogIds]);

  const partnerMarqueeItems = useMemo(() => {
    if (!Array.isArray(partners) || partners.length === 0) return [];
    return Array.from({ length: 3 }, (_, copy) =>
      partners.map((partner, idx) => ({ partner, copy, idx }))
    ).flat();
  }, [partners]);

  const visibleSpecialEvents = useMemo(() => {
    const source = specialCategoryTreks.length > 0 ? specialCategoryTreks : specialEvents;
    return source.filter(hasUpcomingDate);
  }, [specialCategoryTreks, specialEvents]);

  const experienceOptions = useMemo(() => {
    const uniqueCategories = [...new Set(highlightedEvents.map((e) => e.category).filter(Boolean))];
    return uniqueCategories.map((category) => ({
      id: toCategoryId(category),
      label: toCategoryLabel(category)
    }));
  }, [highlightedEvents]);

  const activeExperienceOptions = useMemo(() => {
    if (experienceCategory) {
      return (experienceCategory.subcategories || [])
        .filter((sub) => sub.isActive !== false)
        .map((sub) => ({
          id: sub.slug,
          label: sub.name,
          icon: sub.icon || null
        }));
    }
    return experienceOptions;
  }, [experienceCategory, experienceOptions]);

  useEffect(() => {
    if (activeExperienceOptions.length === 0) return;
    if (!activeExperienceOptions.some((option) => option.id === activeExperience)) {
      setActiveExperience(activeExperienceOptions[0].id);
    }
  }, [activeExperienceOptions, activeExperience]);

  useEffect(() => {
    let cancelled = false;
    const loadHomeCategories = async () => {
      try {
        const [experienceRes, specialRes] = await Promise.all([
          contentAPI.getHomeCategories({ sectionType: 'experience' }),
          contentAPI.getHomeCategories({ sectionType: 'special' })
        ]);
        if (cancelled) return;

        const experienceRows = Array.isArray(experienceRes.data?.data) ? experienceRes.data.data : [];
        const specialRows = Array.isArray(specialRes.data?.data) ? specialRes.data.data : [];

        setExperienceCategory(experienceRows[0] || null);
        setActiveExperience('all');

        if (specialRows[0]?.slug) {
          const specialTreksRes = await dynamicAPI.getTreksBySlug(specialRows[0].slug);
          const specialTreks = Array.isArray(specialTreksRes.data?.data) ? specialTreksRes.data.data : [];
          setSpecialCategoryTreks(specialTreks.filter(hasUpcomingDate).map((trek) => {
            const duration = durationFromBatch(trek.nextBatch?.startDate, trek.nextBatch?.endDate) ||
              durationFromPackages(trek.packages);
            return {
              ...trek,
              title: trek.name,
              imageUrl: optimizeImageUrl(trek.media?.[0]?.url || FALLBACK_EVENT_IMAGE, {
      width: IMAGE_DISPLAY_WIDTH.card
    }),
              category: 'Special Event',
              shortDescription: trek.tagline || trek.description || '',
              duration
            };
          }));
        } else {
          setSpecialCategoryTreks([]);
        }
      } catch (err) {
        if (!cancelled) {
          setExperienceCategory(null);
          setSpecialCategoryTreks([]);
        }
      }
    };
    loadHomeCategories();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const preloadExperienceTreks = async () => {
      if (!experienceCategory?.slug) {
        setExperienceTreksBySlug({});
        return;
      }
      try {
        setExperienceLoading(true);
        const subcategorySlugs = (experienceCategory.subcategories || [])
          .filter((sub) => sub?.isActive !== false && sub?.slug)
          .map((sub) => sub.slug);
        const slugsToPreload = Array.from(new Set([experienceCategory.slug, ...subcategorySlugs]));
        const responses = await Promise.all(
          slugsToPreload.map(async (slug) => {
            const response = await dynamicAPI.getTreksBySlug(slug);
            const rows = Array.isArray(response.data?.data) ? response.data.data : [];
            return [slug, rows.filter(hasUpcomingDate).map(toExperienceCardTrek)];
          })
        );
        if (cancelled) return;
        setExperienceTreksBySlug(Object.fromEntries(responses));
      } catch (err) {
        if (!cancelled) setExperienceTreksBySlug({});
      } finally {
        if (!cancelled) setExperienceLoading(false);
      }
    };
    preloadExperienceTreks();
    return () => { cancelled = true; };
  }, [experienceCategory]);

  return (
    <div className="home-page">
      {/* Hero Banner with Image Gallery/Carousel */}
      {displaySliders.length > 0 ? (
        <section
          className="hero-banner"
          style={{ touchAction: 'pan-y pinch-zoom' }}
          onTouchStart={(e) => {
            if (displaySliders.length <= 1) return;
            heroTouchStartX.current = e.touches[0]?.clientX ?? null;
          }}
          onTouchEnd={(e) => {
            handleHeroSwipeEnd(e.changedTouches[0]?.clientX ?? 0);
          }}
          onPointerDown={(e) => {
            if (displaySliders.length <= 1 || e.pointerType === 'touch') return;
            heroPointerIdRef.current = e.pointerId;
            heroTouchStartX.current = e.clientX;
          }}
          onPointerUp={(e) => {
            if (heroPointerIdRef.current !== e.pointerId) return;
            handleHeroSwipeEnd(e.clientX);
          }}
          onPointerCancel={() => {
            heroTouchStartX.current = null;
            heroPointerIdRef.current = null;
          }}
        >
          <div className="hero-slider">
            {displaySliders.map((slider, index) => (
              <div
                key={slider.id || index}
                className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              >
                {heroImageLoadIndexes.has(index) && (
                  <img
                    src={optimizeImageUrl(slider.imageUrl, { width: IMAGE_DISPLAY_WIDTH.hero })}
                    alt={slider.title || 'Hero Banner'}
                    className="hero-slide-image"
                    fetchpriority={index === 0 ? 'high' : 'auto'}
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                )}
                <div className="hero-overlay">
                  <div className="hero-content">
                    <div className="hero-title-wrap">
                      <h1 className="hero-title">{(slider.title || '')
                        .trim()
                        .split(/\s+/)
                        .filter(Boolean)
                        .map((word, i, words) =>
                          word === 'Experience.' ? (
                            <span key={i} className="hero-title-orange">
                              {word}
                              {i < words.length - 1 ? ' ' : ''}
                            </span>
                          ) : (
                            <span key={i}>
                              {word}
                              {i < words.length - 1 ? ' ' : ''}
                            </span>
                          )
                        )}</h1>
                    </div>
                    <div className="hero-subtitle-wrap">
                      <p className="hero-subtitle">{(slider.subtitle || '').trim()}</p>
                    </div>
                    <Link to={slider.ctaLink || '/events'} className="hero-cta">
                      <span>{slider.ctaText || 'Explore'}</span>
                      <img
                        src={`${import.meta.env.BASE_URL}assets/arrow-up-right.svg`}
                        alt=""
                        className="hero-cta-icon"
                        aria-hidden
                      />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Stats Section - Below Hero */}
      <section className="stats-section">
        <div className="stats-container">
          <div className="stats-left">
            <h3 className="stats-title">{settings.stats_section_title || "India's Largest Trekking Organization"}</h3>
            {settings.stats_section_subtitle ? (
              <p className="stats-subtitle">{settings.stats_section_subtitle}</p>
            ) : null}
            <div className="stats-grid">
              {stats.length > 0 ? (
                stats.map((stat, index) => {
                  const StatIcon =
                    index === 0 ? Users :
                    index === 1 ? HandHeart :
                    index === 2 ? CalendarDays :
                    index === 3 ? Award :
                    Activity;
                  return (
                    <div key={stat.id || index} className="stat-item">
                      <div className="stat-icon">
                        <StatIcon className="stat-icon-svg" size={26} strokeWidth={1.75} />
                      </div>
                      <div>
                        <div className="stat-number">{stat.value}</div>
                        <div className="stat-label">{stat.label}</div>
                      </div>
                    </div>
                  );
                })
              ) : <p className="events-loading">No stats configured.</p>}
            </div>
          </div>
          <div className="stats-right">
            {statsVisualUrl ? (
              isStatsVisualYouTube ? (
                <div className={`stats-media-shell ${statsMediaReady ? 'is-ready' : 'is-loading'}`}>
                  {!statsMediaReady && <div className="stats-media-loader" aria-hidden />}
                  <iframe
                    src={statsYouTubeEmbedUrl}
                    className="stats-section-embed"
                    title="Stats section video"
                    loading="eager"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    onLoad={() => setStatsMediaReady(true)}
                  />
                </div>
              ) : shouldTryStatsAsVideo && !statsVideoErrored ? (
                <div className={`stats-media-shell ${statsMediaReady ? 'is-ready' : 'is-loading'}`}>
                  {!statsMediaReady && <div className="stats-media-loader" aria-hidden />}
                  <video
                    src={statsVisualUrl}
                    className="stats-section-video"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    aria-label="Stats section visual"
                    onLoadedData={() => setStatsMediaReady(true)}
                    onError={() => setStatsVideoErrored(true)}
                  />
                </div>
              ) : isStatsVisualImage && !statsImageErrored ? (
                <div className={`stats-media-shell ${statsMediaReady ? 'is-ready' : 'is-loading'}`}>
                  {!statsMediaReady && <div className="stats-media-loader" aria-hidden />}
                  <img
                    src={statsVisualUrl}
                    alt="Stats section visual"
                    className="stats-section-image"
                    onLoad={() => setStatsMediaReady(true)}
                    onError={() => setStatsImageErrored(true)}
                   loading="lazy" decoding="async" />
                </div>
              ) : (
                <div className="team-photo-placeholder"></div>
              )
            ) : (
              <div className="team-photo-placeholder"></div>
            )}
          </div>
        </div>
      </section>

      {/* Highlighted Events */}
      <section className="highlighted-events">
        <div className="section-container">
          <div className="highlighted-events-header">
            <h2 className="highlighted-events-title">{settings.highlighted_events_title || 'Highlighted Events'}</h2>
            <p className="highlighted-events-subtitle">{settings.highlighted_events_subtitle || 'Recommended camps by our Instructors.'}</p>
          </div>
          <div
            ref={highlightedScrollRef}
            className="events-carousel highlighted-events-carousel"
          >
            {highlightedLoading ? (
              <div className="events-loading">
                <LoadingIndicator label="Loading highlighted events..." />
              </div>
            ) : highlightedEvents.length > 0 ? (
              <div className="home-carousel-inner home-carousel-inner--highlighted">
                {highlightedEvents.map((item) => (
                  <a
                    key={item.id}
                    href={item.targetUrl}
                    className="highlighted-event-card"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Highlighted event"
                  >
                    {/* Inner clips the image; outer keeps full box-shadow (overflow:hidden clips shadows). */}
                    <div className="highlighted-event-card-inner">
                      <div className="highlighted-event-card-bg">
                        <img
                          src={optimizeImageUrl(item.imageUrl || FALLBACK_EVENT_IMAGE, {
                            width: IMAGE_DISPLAY_WIDTH.card
                          })}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          onError={withImageFallback}
                        />
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            ) : <div className="events-loading">No highlighted events configured.</div>}
          </div>
        </div>
      </section>

      {/* Choose Your Experience */}
      <section className="choose-experience">
        <div className="section-container">
          <h2 className="section-title choose-experience-title-heading">
            <span className="choose-experience-title-line choose-experience-title-line--1">{chooseExperienceHeading.line1}</span>
            {chooseExperienceHeading.line2 ? (
              <span className="choose-experience-title-line choose-experience-title-line--2">{chooseExperienceHeading.line2}</span>
            ) : null}
          </h2>
          {settings.choose_experience_subtitle && (
            <p className="section-subtitle">
              {settings.choose_experience_subtitle}
            </p>
          )}
          <div className="experience-tabs">
            {activeExperienceOptions.map((option) => (
              <button
                key={option.id}
                className={`tab-btn ${activeExperience === option.id ? 'active' : ''}`}
                onClick={() => {
                  if (activeExperience !== option.id) setActiveExperience(option.id);
                }}
              >
                {option.icon && SUBCATEGORY_ICON_MAP[option.icon] && (
                  <span className="experience-tab-icon">{SUBCATEGORY_ICON_MAP[option.icon]}</span>
                )}
                <span>{option.label}</span>
              </button>
            ))}
          </div>
          {(() => {
            const experienceKey = activeExperience === 'all'
              ? experienceCategory?.slug
              : activeExperience;
            const filteredEvents = experienceCategory
              ? (experienceTreksBySlug[experienceKey] || []).slice(0, 8)
              : highlightedEvents
                .filter(hasUpcomingDate)
                .filter((event) => !activeExperience || toCategoryId(event.category) === activeExperience)
                .slice(0, 8);
            const carouselClass = `experience-events-carousel${filteredEvents.length > 3 ? ' experience-carousel-start' : ''}`;

            if (experienceLoading) {
              return (
                <div className="events-loading">
                  <LoadingIndicator label="Loading events..." />
                </div>
              );
            }

            if (!filteredEvents.length) {
              return <div className="events-loading">No events configured for this category.</div>;
            }

            return (
              <div key={activeExperience} className="experience-tab-panel">
                <div className={carouselClass}>
                  <div className="home-carousel-inner home-carousel-inner--events">
                    {filteredEvents.map((event) => (
                      <EventCard
                        key={event.id}
                        event={{
                          ...event,
                          batchId: event.nextBatch?.id ?? event.batchId,
                          slug: event.slug || event.id,
                          imageUrl: optimizeImageUrl(
                            event.imageUrl || event.thumbnailUrl || FALLBACK_EVENT_IMAGE,
                            { width: IMAGE_DISPLAY_WIDTH.card }
                          ),
                          shortDescription: event.tagline || event.description || event.shortDescription || '',
                          location: event.location || event.region || event.baseCamp,
                          startDate: event.startDate || event.nextBatch?.startDate,
                          price: event.price ?? event.nextBatch?.price,
                          brochureUrl: event.brochureUrl || null,
                          departureDates: Array.isArray(event.departureDates) ? event.departureDates : undefined
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* Why Adventure Seeks Love Invincible */}
      <section className="why-invincible">
        <div className="section-container">
          <h2 className="section-title">{settings.why_section_title || 'Why Adventure Seeks Love Invincible'}</h2>
          {settings.why_section_subtitle && (
            <p className="section-subtitle">
              {settings.why_section_subtitle}
            </p>
          )}
          <div className="why-content">
            <div className="why-left">
              <div
                className="community-card"
                style={whyCommunityImageUrl
                  ? {
                      backgroundImage: hasCommunityText
                        ? `linear-gradient(135deg, rgba(45,55,72,0.30) 0%, rgba(74,85,104,0.30) 100%), url(${whyCommunityImageUrl})`
                        : `url(${whyCommunityImageUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }
                  : undefined}
              >
                {String(communityTitle || '').trim() ? <h3>{communityTitle}</h3> : null}
                {String(communitySubtitle || '').trim() ? <p>{communitySubtitle}</p> : null}
              </div>
            </div>
            <div className="why-right">
              <div
                ref={testimonialsScrollRef}
                className="testimonials-scroll-wrap"
                aria-label="Testimonials auto-scrolling"
                onScroll={keepTestimonialsLoopSeamless}
                onPointerEnter={(e) => {
                  // Only pause for real hover (mouse/pen). Touch shouldn't permanently pause.
                  if (e.pointerType === 'mouse' || e.pointerType === 'pen') {
                    testimonialsAutoPausedRef.current = true;
                  }
                }}
                onPointerLeave={(e) => {
                  if (e.pointerType === 'mouse' || e.pointerType === 'pen') {
                    testimonialsAutoPausedRef.current = false;
                  }
                }}
              >
                <div className="testimonials-track">
                  {(() => {
                    const list = testimonials;
                    if (!list.length) {
                      return <div className="events-loading">No testimonials configured.</div>;
                    }
                    const renderCard = (testimonial, keySuffix) => {
                      const formattedDate = testimonial.createdAt
                        ? new Date(testimonial.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                        : testimonial.date || 'Recently';
                      const eventOrDate = (testimonial.eventName && testimonial.eventName.trim())
                        ? testimonial.eventName.trim()
                        : `On: ${formattedDate}`;
                      return (
                        <div key={`${testimonial.id}-${keySuffix}`} className="testimonial-card">
                          <div className="testimonial-header">
                            <div className="testimonial-avatar">
                              <img
                                src={optimizeImageUrl(
                                  testimonial.imageUrl || testimonial.avatar || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
                                  { width: IMAGE_DISPLAY_WIDTH.thumb }
                                )}
                                alt={testimonial.name}
                               loading="lazy" decoding="async" />
                            </div>
                            <div className="testimonial-info">
                              <div className="testimonial-name">{testimonial.name}</div>
                              <div className="testimonial-date">{eventOrDate}</div>
                            </div>
                            <div
                              className="testimonial-rating-pill"
                              aria-label={`${testimonial.rating || 5} out of 5 stars`}
                            >
                              <span className="testimonial-rating-icon">★</span>
                              <span className="testimonial-rating-value">
                                {testimonial.rating ?? 5}
                              </span>
                            </div>
                          </div>
                          <p className="testimonial-review">{testimonial.quote || testimonial.review || testimonial.content || testimonial.message}</p>
                          {(testimonial.galleryImages || testimonial.images) && (testimonial.galleryImages || testimonial.images).length > 0 && (
                            <div className="testimonial-images">
                              {(testimonial.galleryImages || testimonial.images).slice(0, 4).map((img, idx) => (
                                <div key={idx} className="testimonial-image-thumb">
                                  <img
                                    src={optimizeImageUrl(img, { width: IMAGE_DISPLAY_WIDTH.thumb })}
                                    alt={`${testimonial.name} review ${idx + 1}`}
                                    loading="lazy"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    };
                    return (
                      <>
                        {Array.from({ length: testimonialLoopCopies }, (_, copy) => (
                          <div key={`copy-${copy}`} className="testimonials-copy">
                            {list.map((t, i) => renderCard(t, `${copy}-${i}`))}
                          </div>
                        ))}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Special Events */}
      <section className="special-events">
        <div className="section-container">
          <h2 className="section-title special-events-title">{settings.special_events_title || 'Special Events: A Once In A Lifetime Opportunity'}</h2>
          <p className="section-subtitle special-events-subtitle">{settings.special_events_subtitle || 'Unique Experiences Curated For The Bold'}</p>
          {(() => {
            const list = visibleSpecialEvents;
            const specialCarouselClass = `special-events-carousel${list.length > 3 ? ' experience-carousel-start' : ''}`;
            if (!list.length) {
              return <div className="events-loading">No special events configured.</div>;
            }
            return (
              <div className={specialCarouselClass}>
                <div className="home-carousel-inner home-carousel-inner--events">
                  {list.map((event) => (
                    <EventCard
                      key={event.id}
                      event={{
                        ...event,
                        batchId: event.nextBatch?.id ?? event.batchId,
                        slug: event.slug || event.id,
                        imageUrl: optimizeImageUrl(
                          event.imageUrl || event.thumbnailUrl || FALLBACK_EVENT_IMAGE,
                          { width: IMAGE_DISPLAY_WIDTH.card }
                        ),
                        shortDescription: event.tagline || event.description || event.shortDescription || '',
                        location: event.location || event.region || event.baseCamp,
                        startDate: event.startDate || event.nextBatch?.startDate,
                        price: event.price ?? event.nextBatch?.price,
                        brochureUrl: event.brochureUrl || null,
                        departureDates: Array.isArray(event.departureDates) ? event.departureDates : undefined
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* Latest Blogs */}
      <section className="latest-blogs">
        <div className="section-container">
          <h2 className="section-title">{settings.blogs_section_title || 'Latest Blogs'}</h2>
          <p className="section-subtitle">{settings.blogs_section_subtitle || 'Stories From The Trails'}</p>
          <div className={`home-blogs-grid${displayedBlogs.length > 3 ? ' home-blogs-grid-start' : ''}`}>
            {displayedBlogs.length > 0 ? displayedBlogs.map(blog => {
              // Format date from publishedAt or createdAt
              const blogDate = blog.publishedAt || blog.createdAt || blog.date;
              const formattedDate = blogDate 
                ? new Date(blogDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                : blog.date || 'Recently';

              return (
                <BlogCard
                  key={blog.id}
                  to={`/blogs/${blog.slug || blog.id}`}
                  image={optimizeImageUrl(
                    blog.imageUrl ||
                      blog.thumbnailUrl ||
                      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400',
                    { width: IMAGE_DISPLAY_WIDTH.card }
                  )}
                  category={(blog.category || 'BLOG').toUpperCase()}
                  title={blog.title}
                  date={formattedDate}
                  author={blog.author || 'Admin'}
                  excerpt={blog.shortDescription ? String(blog.shortDescription).trim() : ''}
                />
              );
            }) : <div className="events-loading">No blogs configured.</div>}
          </div>
        </div>
      </section>

      {/* Frequently Asked Questions (GEO / AEO) */}
      <section className="home-faq-section" aria-labelledby="home-faq-title">
        <div className="section-container">
          <h2 id="home-faq-title" className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle">Essential Information About Trekking With Invincible NGO</p>
          <div className="home-faq-grid">
            {HOME_FAQS.map((faq, index) => (
              <details key={index} className="home-faq-item" open={index === 0}>
                <summary className="home-faq-question">
                  <span>{faq.question}</span>
                  <ChevronDown className="home-faq-icon" aria-hidden="true" />
                </summary>
                <div className="home-faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="partners">
        <div className="section-container">
          <h2 className="section-title">{settings.partners_section_title || 'Our Partners & Associations'}</h2>
          {settings.partners_section_subtitle && (
            <p className="section-subtitle">
              {settings.partners_section_subtitle}
            </p>
          )}
          <div className="partners-logos">
            {partners.length > 0 ? (
              <div className="partners-marquee" aria-label="Partners">
                <div className="partners-marquee-track">
                  {partnerMarqueeItems.map(({ partner, copy, idx }) => (
                    <a
                      key={`${partner.id || partner.name || 'partner'}-${copy}-${idx}`}
                      className="partner-item"
                      href={partner.websiteUrl || undefined}
                      target={partner.websiteUrl ? '_blank' : undefined}
                      rel={partner.websiteUrl ? 'noopener noreferrer' : undefined}
                      aria-label={copy === 0 ? partner.name : undefined}
                      aria-hidden={copy > 0 ? true : undefined}
                      tabIndex={copy > 0 ? -1 : undefined}
                    >
                      {partner.logoUrl ? (
                        <img
                          src={optimizeImageUrl(partner.logoUrl, { width: IMAGE_DISPLAY_WIDTH.thumb })}
                          alt={partner.name}
                          className="partner-logo"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <span className="partner-name-text">{partner.name}</span>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            ) : <div className="events-loading">No partners configured.</div>}
          </div>
        </div>
      </section>

      <a
        href={homeWhatsappHref}
        className="home-whatsapp-float"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
      >
        <svg className="home-whatsapp-float-icon" viewBox="0 0 24 24" aria-hidden>
          <path
            fill="currentColor"
            d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.472-.149-.672.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.672-1.611-.922-2.206-.242-.579-.487-.501-.672-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
          />
        </svg>
      </a>
    </div>
  );
};

export default Home;
